import { Popper, PopperPlacementType } from "@mui/base/Popper";
import useMuiTheme from "@mui/material/styles/useTheme";
import { useAnimateHeight } from "@redotech/react-animation/transition";
import { useSize } from "@redotech/react-util/size";
import classnames from "classnames";
import {
  ForwardedRef,
  forwardRef,
  memo,
  ReactNode,
  useLayoutEffect,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import * as dropdownCss from "./dropdown.module.css";
import { Flex, FlexProps } from "./flex";
import * as darkModeCss from "./theme/component-colors-dark.module.css";
import { Tooltip } from "./tooltip/tooltip";

export const Dropdown = memo(
  forwardRef(function Dropdown(
    {
      anchor,
      children,
      open,
      fitToAnchor = true,
      flip = true,
      placement = undefined,
      darkMode = false,
      constrainHeight = true,
      flexProps,
      className,
    }: {
      anchor: HTMLElement | null;
      children: ReactNode | ReactNode[];
      open: boolean;
      fitToAnchor?: boolean;
      flip?: boolean;
      placement?: PopperPlacementType;
      darkMode?: boolean;
      constrainHeight?: boolean;
      flexProps?: FlexProps;
      className?: string;
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) {
    const muiTheme = useMuiTheme();
    const [dropdown, setDropdown] = useState<HTMLElement | null>(null);

    const [animateStyles, animating] = useAnimateHeight(dropdown);

    const debounceOpen = useDebounce(open, 1);

    const size = useSize(anchor);

    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    useLayoutEffect(() => {
      if (!open || !anchor || !constrainHeight) {
        setMaxHeight(undefined);
        return;
      }
      const rect = anchor.getBoundingClientRect();
      const gapPx = 24;
      const availableBelow = window.innerHeight - rect.bottom - gapPx;
      const availableAbove = rect.top - gapPx;
      const available = Math.max(availableBelow, availableAbove);
      setMaxHeight(Math.min(415, Math.max(available, 0)));
    }, [open, anchor, constrainHeight]);

    const width = fitToAnchor ? size && `${size.width}px` : undefined;

    return (
      <Popper
        anchorEl={anchor}
        className={dropdownCss.root}
        container={
          muiTheme.components?.MuiPopper?.defaultProps?.container ?? undefined
        }
        modifiers={[{ name: "flip", enabled: flip }]}
        open={open || debounceOpen || animating}
        placement={placement} // Avoid cursed type check error when trying to import the PopupPlacement type
        ref={ref}
      >
        <Flex
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          className={classnames(
            dropdownCss.dropdown,
            darkMode && darkModeCss.darkMode,
            {
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.constrainHeight]: constrainHeight,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.leave]: !open || !debounceOpen,
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              [dropdownCss.animating]: animating,
            },
            className,
          )}
          dir="column"
          id="dropdown"
          p="md"
          radius="md"
          ref={setDropdown}
          style={{ ...animateStyles, width, maxHeight: maxHeight ?? undefined }}
          {...flexProps}
        >
          {(animating || (open && debounceOpen)) && children}
        </Flex>
      </Popper>
    );
  }),
);

export const DropdownOption = memo<{
  action?: () => void;
  children: ReactNode | ReactNode[];
  fontFamily?: string;
  disabled?: boolean;
  stopPropagation?: boolean;
  darkMode?: boolean;
  tooltip?: string;
}>(function DropdownOption({
  action,
  children,
  fontFamily,
  disabled,
  stopPropagation,
  darkMode,
  tooltip,
}) {
  return tooltip ? (
    <Tooltip arrow title={tooltip}>
      <button
        className={classnames(dropdownCss.option, {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          [dropdownCss.dark]: darkMode,
        })}
        disabled={disabled}
        onClick={(event) => {
          if (stopPropagation) {
            event.stopPropagation();
          }
          action?.();
        }}
        style={{ fontFamily }}
        type="button"
      >
        {children}
      </button>
    </Tooltip>
  ) : (
    <button
      className={classnames(dropdownCss.option, {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        [dropdownCss.dark]: darkMode,
      })}
      disabled={disabled}
      onClick={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
        action?.();
      }}
      style={{ fontFamily }}
      type="button"
    >
      {children}
    </button>
  );
});
